// 创建账号
import { useState, useEffect, useRef, useCallback } from 'react';
import { InputNumber, Input, Button } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import EmilIcon from '@/assets/img/emilIcon.png';
import refleshIcon from '@/assets/img/refleshIcon.png';
import Login from '@/modules/auth/api/main';
import { useAppLocation, useNavigation } from '@/common/hooks/navigation';
import { useAppDispatch, useAppSelector } from '@/common/hooks/store';
import { loginUser, getUserInfo } from '@/modules/auth/store';
import { saveSurveyData } from '@/modules/forms/store';
import { showLoading, hideLoading } from '@/common/components/LoadingTemp';
import { getUtmParamsForApi } from '@/utils/utm';
import './index.scss';

export default function VerifyEmilCode(props) {
  const { onSwitch, onFetchCodeCallBack, params } = props;
  const [codes, setCodes] = useState([]);
  const [timeCount, setTimeCount] = useState(60); // 倒计时
  const inputsRef = useRef([]);
  const TimeRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const location = useAppLocation();
  const navigation = useNavigation();
  const { query } = location;
  // const [isSendCodeSuccess, setIsSendCodeSuccess] = useState(false);
  const [checkCodeMessage, setCheckCodeMessage] = useState(null);
  const diagnosisInfo = useAppSelector(state => state.diagnosis);
  useEffect(() => {
    clear();
    setCodes(Array.from({ length: 6 }, () => ''));
    setCheckCodeMessage(null);
    return () => {
      clear();
    };
  }, []);

  useEffect(() => {
    if (params.origin) {
      if (params.origin === 'emailCodeSignIn' || params.origin === 'reset') {
        onSendCode();
      } else {
        onRefleshCode();
      }
    }
  }, [params]);

  // 监听inputsRef数组第一个元素的input框在页面第一次渲染时候自动聚焦
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (codes.length && inputsRef.current.length) {
      if (codes.every(v => !v)) {
        inputsRef.current[0]?.focus();
        setHasSubmitted(false); // Reset when codes are cleared
      }
      // 验证码全部填写完整发出接口请求
      if (codes.every(v => v) && !loading && !checkCodeMessage && !hasSubmitted) {
        // 添加小延迟确保用户输入完成
        const timer = setTimeout(() => {
          setHasSubmitted(true);
          onFetchCode();
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [codes, loading, hasSubmitted]);

  useEffect(() => {
    if (timeCount == 0) {
      clear();
    }
  }, [timeCount]);

  // 刷新验证码
  const onRefleshCode = () => {
    setCodes(Array.from({ length: 6 }, () => ''));
    inputsRef.current[0]?.focus();
    // 倒计时
    setTimeCount(59);
    TimeRef.current = setInterval(() => setTimeCount(t => --t), 1000);
  };

  // 点击刷新code
  const onReflesh = () => {
    setCheckCodeMessage(null);
    onSendCode('reflesh');
  };
  // 发送验证码
  const onSendCode = type => {
    setLoading(true);
    const loginType = {
      createAccount: {
        url: 'checkAccount',
        params: {
          email: params.email,
          first_name: params.first_name,
        },
      },
      emailCodeSignIn: {
        url: 'sendEmailCode',
        params: {
          email: params.email,
        },
      },
      reset: {
        url: 'fogotPassword',
        params: {
          email: params.email,
        },
      },
    };

    const fetch = loginType[params.origin].url;
    const query = loginType[params.origin].params;
    Login[fetch](query, true)
      .then(res => {
        onRefleshCode();
        if (type == 'reflesh') {
          setCheckCodeMessage({
            type: 'success',
            message: 'Email envoyé à nouveau !',
          });
        }
      })
      .catch(err => {
        setCheckCodeMessage({
          type: 'error',
          message: err?.message,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 接口校验
  const onFetchCode = () => {
    // 接口校验成功
    if (loading) return;

    setLoading(true);

    const requestData = {
      ...params,
      code: codes.join(''),
    };

    console.log('Sending checkEmailCode with data:', requestData);

    Login.checkEmailCode(requestData)
      .then(res => {
        // Vérifier le statut HTTP et le code de réponse dans les données
        const httpStatus = res?.status;
        const responseCode = res?.data?.code;

        console.log('checkEmailCode response:', {
          httpStatus,
          responseCode,
          data: res?.data,
          fullResponse: res
        });

        if (httpStatus === 204 || responseCode === 200) {
          // 验证成功 (204 No Content ou 200 OK)，清除错误消息并继续
          console.log('Code validation successful, calling onVerifySuccess');
          setCheckCodeMessage(null);
          onVerifySuccess();
        } else {
          // Code incorrect ou autre erreur
          console.log('Code validation failed');
          setCheckCodeMessage({
            type: 'error',
            message: res?.data?.message || 'Code de vérification incorrect',
          });
        }
      })
      .catch(err => {
        // L'interceptor a rejeté la réponse (code 2000, etc.)
        const errorMessage = err?.message || 'Code de vérification incorrect';
        setCheckCodeMessage({
          type: 'error',
          message: errorMessage,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // code校验成功后-注册/重置密码
  const onVerifySuccess = () => {
    // 注册
    if (params.origin == 'createAccount') {
      // Get UTM parameters for tracking user acquisition source
      const utmParams = getUtmParamsForApi();

      Login.register({
        ...params,
        code: codes.join(''),
        promotion_code: query?.code || '',
        ...utmParams,
      }).then(res => {
        // 创建完账号直接登录-登录成功-获取用户信息后跳转diagnosis
        dispatch(loginUser({
          email: params.email,
          password: params.password,
        }))
          .unwrap()
          .then(() => {
            return dispatch(getUserInfo()).unwrap();
          })
          .then(userInfoRes => {
            onSaveFormData(userInfoRes);
          });
      });
    } else if (params.origin == 'emailCodeSignIn') {
      // 使用邮箱登录
      emailCOdeSignIn();
    } else {
      // 重置密码
      onFetchCodeCallBack && onFetchCodeCallBack(codes.join(''));
    }
  };

  // 邮箱登录
  const emailCOdeSignIn = () => {
    // setLoading(true);
    showLoading();
    Login.verificationLogin({
      code: codes.join(''),
      email: params.email,
    }).then(res => {
      const token = `${res.data?.data?.token_type} ${res.data?.data?.access_token}`;
      localStorage.setItem('token', token);
      dispatch(getUserInfo())
        .unwrap()
        .then(userInfoRes => {
          onSaveFormData(userInfoRes);
        })
        .finally(() => {
          hideLoading();
          clear();
        });
      // navigation.replace('/');
    });
  };

  // 登录时需要判断缓存是否有问卷填写答案-有的话需要带上
  const onSaveFormData = data => {
    const { has_diagnosis, has_legal } = data;
    const isAccept = window.localStorage.getItem('isAccept');
    const diagnosisFormDataIsEnd = window.localStorage.getItem(
      'diagnosisFormDataIsEnd',
    );
    const diagnosisFormData = window.localStorage.getItem('diagnosisFormData');
    const leagalFormData = window.localStorage.getItem('leagalFormData');
    const leagalFormDataIsEnd = window.localStorage.getItem(
      'leagalFormDataIsEnd',
    );
    const promiseRequest = [];
    if (has_diagnosis !== 1 && has_legal !== 1) {
      // 缓存存储、store存储判断
      if (
        (isAccept === '1' &&
          diagnosisFormDataIsEnd == 'true' &&
          diagnosisFormData &&
          JSON.parse(diagnosisFormData)?.length) ||
        diagnosisInfo?.diagnosisFormData?.length
      ) {
        promiseRequest.push(1);
      }
      if (
        (isAccept === '1' &&
          leagalFormDataIsEnd == 'true' &&
          leagalFormData &&
          JSON.parse(leagalFormData)?.length) ||
        diagnosisInfo?.leagalFormData?.length
      ) {
        promiseRequest.push(2);
      }
      if (promiseRequest.length == 2) {
        dispatch(saveSurveyData()).unwrap().then(res => {
          const from = new URLSearchParams(location.search).get('from') || '/';
          // 重定向回之前的页面
          navigation.replace(from);
          // navigation.replace('/home');
        });
      } else {
        navigation.replace('/diagnosis');
      }
    } else {
      if (has_diagnosis == 1 && has_legal == 1) {
        // navigation.replace('/home');
        const from = new URLSearchParams(location.search).get('from') || '/';
        // 重定向回之前的页面
        navigation.replace(from);
      } else if (has_diagnosis == 1 && has_legal !== 1) {
        navigation.replace('/legalForm');
      } else {
        navigation.replace('/diagnosis');
      }
    }
  };

  function clear() {
    clearInterval(TimeRef.current);
    TimeRef.current = null;
    setTimeCount(60);
  }

  const onHandleSwitch = () => {
    clear();
    onSwitch && onSwitch('signIn');
  };

  // 输入框onchange
  const onEmilCodeChange = useCallback((e, index) => {
    // 清除之前的错误消息
    if (checkCodeMessage?.type === 'error') {
      setCheckCodeMessage(null);
    }

    // Reset submission flag when user starts typing
    setHasSubmitted(false);

    const currentVal = e.target.value.match(/\s+/g) ? '' : e.target.value; // 判断当前值是否包含空格
    if (currentVal) {
      inputsRef.current[index + 1]?.focus();
    }
    setCodes(pre => {
      const newData = [...pre];
      newData[index] = currentVal;
      return newData;
    });
  }, [checkCodeMessage]);

  // 粘贴
  const onHandlePase = useCallback(e => {
    e.preventDefault(); // Empêcher le comportement par défaut

    // 清除之前的错误消息
    setCheckCodeMessage(null);

    // Reset submission flag when pasting
    setHasSubmitted(false);

    const pasteVal = e.clipboardData.getData('text').replace(/\s+/g, '');

    // Prendre seulement les 6 premiers caractères
    const cleanVal = pasteVal.slice(0, 6);

    const newData = Array.from(
      { length: 6 },
      (v, i) => cleanVal.charAt(i) || '',
    );
    setCodes(newData);
    const foucusIndex = Math.min(cleanVal.length, 5);
    inputsRef.current[foucusIndex]?.focus();
  }, []);

  // 删除事件
  const onHandleKeyDown = useCallback(
    (e, index) => {
      const { key } = e;
      if (key !== 'Backspace' && key !== 'Delete') {
        return;
      }
      if (codes[index]) {
        setCodes(pre => {
          const newData = [...pre];
          newData[index] = '';
          return newData;
        });
      }
      if (index > 0) {
        setCodes(pre => {
          const newData = [...pre];
          newData[index - 1] = '';
          return newData;
        });
        inputsRef.current[index - 1]?.focus();
      }
    },
    [codes],
  );
  return (
    <div className="loginPage_right_main loginPage_right_main_verifyEmilCode">
      <div className="goBackBox">
        <Button
          className="goBackBtn"
          onClick={onHandleSwitch}
          icon={<LeftOutlined />}
        />
        <span className="goBackText">Retour</span>
      </div>
      <div className="emilImgBox">
        <img src={EmilIcon} alt="" className="emilImg" />
      </div>
      <div className="tittle">Vérifier mon email</div>
      <div className="description">
        Merci de bien vouloir saisir le code de vérification à 6 chiffres envoyé
        par email.
      </div>
      <div className="emilAddress">{params?.email}</div>
      <div className="emilCodeBox">
        {codes.length &&
          codes.map((item, index) => (
            <Input
              key={index}
              maxLength={1}
              value={item}
              className="emilCodeInput"
              type="number"
              controls={false}
              inputMode="numeric"
              ref={ref => (inputsRef.current[index] = ref)}
              onPaste={onHandlePase}
              onKeyDown={e => onHandleKeyDown(e, index)}
              onChange={e => onEmilCodeChange(e, index)}
              onFocus={e => e.target.select()}
            />
          ))}
        <Button
          disabled={timeCount !== 60}
          shape="circle"
          onClick={onReflesh}
          className="refleshBtn"
          icon={timeCount == 60 ? <img src={refleshIcon} /> : null}
        >
          {timeCount !== 60 ? timeCount : null}
        </Button>
      </div>
      <div className={`tipsText ${checkCodeMessage?.type}`}>
        {checkCodeMessage?.message}
      </div>
    </div>
  );
}
